<template>
  <div class="seller-dashboard">
    <v-container fluid class="pa-4 pa-md-6">
      <!-- Hero Section -->
      <div class="hero-section">
        <div class="hero-content">
          <div class="hero-badge">
            <v-icon small color="white" class="mr-1">mdi-store</v-icon>
            Vendor Hub
          </div>
          <h1 class="hero-title">Dashboard Overview</h1>
          <p class="hero-subtitle">
            Track your earnings, manage inventory, and transfer funds instantly.
          </p>
          <div class="hero-actions">
            <v-btn 
              color="white" 
              class="primary--text font-weight-bold" 
              rounded 
              elevation="4"
              large
              :to="{ name: 'business_seller.add_product' }"
            >
              <v-icon left>mdi-plus-circle</v-icon>
              Add Product
            </v-btn>
            <v-btn 
              outlined 
              color="white" 
              rounded 
              large
              class="ml-3 glass-btn"
              @click="openTransferDialog"
            >
              <v-icon left>mdi-bank-transfer</v-icon>
              Transfer Balance
            </v-btn>
          </div>
        </div>
        <div class="hero-decoration"></div>
      </div>

      <!-- Stats Cards -->
      <div class="stats-grid">
        <v-card class="stat-card stat-balance" elevation="8" rounded="xl">
          <div class="stat-bg-icon">
            <v-icon size="80" color="white" style="opacity: 0.1">mdi-wallet-outline</v-icon>
          </div>
          <div class="stat-content">
            <div class="stat-header">
              <div class="stat-icon-wrapper primary-gradient">
                <v-icon color="white">mdi-wallet</v-icon>
              </div>
              <v-chip 
                x-small 
                color="white" 
                text-color="primary" 
                class="font-weight-bold"
                v-if="parseFloat(dashboard.seller_balance) > 0"
              >
                Available
              </v-chip>
            </div>
            <div class="stat-body mt-3">
              <div class="stat-label text-uppercase text-caption font-weight-medium opacity-8">
                Vendor Balance
              </div>
              <div class="stat-value text-h4 font-weight-bold mt-1">
                {{ formatCurrency(dashboard.seller_balance) }}
              </div>
            </div>
            <v-btn 
              block 
              color="white" 
              class="primary--text mt-4 font-weight-bold text-none" 
              rounded
              elevation="2"
              @click="openTransferDialog"
              :disabled="parseFloat(dashboard.seller_balance) <= 0"
            >
              Transfer Funds
            </v-btn>
          </div>
        </v-card>

        <v-card class="stat-card stat-secondary" elevation="4" rounded="xl">
          <div class="stat-icon-wrapper success-gradient">
            <v-icon color="white">mdi-chart-line</v-icon>
          </div>
          <div class="stat-body mt-3">
            <div class="stat-label text-uppercase text-caption font-weight-medium grey--text">
              Total Earned
            </div>
            <div class="stat-value text-h5 font-weight-bold mt-1 grey--text text--darken-3">
              {{ formatCurrency(dashboard.total_earned) }}
            </div>
            <div class="stat-trend mt-1 success--text text-caption">
              <v-icon x-small color="success">mdi-trending-up</v-icon>
              Lifetime revenue
            </div>
          </div>
        </v-card>

        <v-card class="stat-card stat-secondary" elevation="4" rounded="xl">
          <div class="stat-icon-wrapper info-gradient">
            <v-icon color="white">mdi-package-variant-closed</v-icon>
          </div>
          <div class="stat-body mt-3">
            <div class="stat-label text-uppercase text-caption font-weight-medium grey--text">
              Products Listed
            </div>
            <div class="stat-value text-h5 font-weight-bold mt-1 grey--text text--darken-3">
              {{ dashboard.product_count || 0 }}
            </div>
            <div class="stat-trend mt-1 info--text text-caption">
              <v-icon x-small color="info">mdi-check-circle</v-icon>
              {{ products.length }} active
            </div>
          </div>
        </v-card>
      </div>

      <!-- Products Section -->
      <div class="content-section">
        <v-card class="content-card" elevation="2" rounded="xl">
          <v-card-title class="card-header">
            <div class="header-title">
              <div class="d-flex align-center">
                <div class="header-icon mr-3 primary--text">
                  <v-icon large>mdi-package-variant</v-icon>
                </div>
                <div>
                  <div class="text-h6 font-weight-bold">My Products</div>
                  <div class="text-caption grey--text">
                    {{ filteredProducts.length }} product{{ filteredProducts.length > 1 ? 's' : '' }} in inventory
                  </div>
                </div>
              </div>
            </div>
            <v-spacer></v-spacer>
            <v-text-field
              v-model="productSearch"
              append-icon="mdi-magnify"
              label="Search products..."
              single-line
              hide-details
              dense
              filled
              rounded
              class="search-field shrink"
              style="max-width: 300px;"
            ></v-text-field>
          </v-card-title>

          <v-divider></v-divider>

          <div v-if="productsLoading" class="pa-4">
            <v-skeleton-loader
              v-for="n in 5"
              :key="n"
              type="list-item-avatar-three-line"
              class="mb-2"
            ></v-skeleton-loader>
          </div>

          <v-alert 
            v-else-if="productsError" 
            type="error" 
            text 
            prominent
            class="ma-4"
            border="left"
          >
            <div class="font-weight-bold">Unable to load products</div>
            <div class="text-caption">{{ productsError }}</div>
            <v-btn x-small text color="error" class="mt-2" @click="loadProducts">
              Retry
            </v-btn>
          </v-alert>

          <div v-else-if="!filteredProducts.length" class="empty-state-container">
            <div class="empty-state-content">
              <div class="empty-illustration">
                <v-icon size="64" color="grey lighten-2">mdi-package-variant-closed</v-icon>
              </div>
              <h3 class="text-h6 grey--text text--darken-2 font-weight-medium">No products yet</h3>
              <p class="text-body-2 grey--text text-center max-width-400 mt-2">
                Start building your inventory by adding your first product to the marketplace.
              </p>
              <v-btn 
                color="primary" 
                rounded 
                class="mt-4"
                :to="{ name: 'business_seller.add_product' }"
              >
                <v-icon left>mdi-plus</v-icon>
                Add Your First Product
              </v-btn>
            </div>
          </div>

          <div v-else class="table-container">
            <table class="products-table">
              <thead>
                <tr>
                  <th class="text-left pl-6">Product</th>
                  <th class="text-right">Price</th>
                  <th class="text-left hidden-sm-and-down">Added On</th>
                  <th class="text-center pr-6" style="width: 230px;">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr 
                  v-for="item in filteredProducts" 
                  :key="item.id"
                  class="product-row"
                >
                  <td class="pl-6">
                    <div class="d-flex align-center">
                      <v-avatar size="48" rounded="lg" class="mr-4 product-avatar">
                        <v-img :src="item.get_image || '/placeholder-product.png'" contain>
                          <template v-slot:placeholder>
                            <v-icon color="grey lighten-2">mdi-image-off</v-icon>
                          </template>
                        </v-img>
                      </v-avatar>
                      <div>
                        <div class="text-subtitle-2 font-weight-semibold text--primary">
                          {{ item.name }}
                        </div>
                        <div class="text-caption grey--text">
                          ID: #{{ item.id }}
                          <v-chip v-if="item.markup_percentage && parseFloat(item.markup_percentage) > 0"
                            x-small color="teal" dark class="ml-1">
                            +{{ item.markup_percentage }}% markup
                          </v-chip>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td class="text-right">
                    <div class="text-body-2 font-weight-bold">{{ formatCurrency(item.price) }}</div>
                  </td>
                  <td class="text-left hidden-sm-and-down">
                    <span class="text-caption grey--text">{{ item.get_date_str }}</span>
                  </td>
                  <td class="text-center pr-6">
                    <v-btn
                      small
                      outlined
                      color="teal"
                      rounded
                      elevation="1"
                      class="text-none font-weight-medium"
                      @click="openMarkupDialog(item)"
                    >
                      <v-icon small left>mdi-percent</v-icon>
                      Markup
                    </v-btn>
                    <v-btn
                      small
                      outlined
                      color="primary"
                      rounded
                      elevation="1"
                      class="text-none font-weight-medium ml-1"
                      @click="openProductDialog(item)"
                    >
                      <v-icon small left>mdi-cog-outline</v-icon>
                      Manage
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div v-if="filteredProducts.length > 0" class="pa-4 text-center grey--text text-caption">
            Showing all {{ filteredProducts.length }} product{{ filteredProducts.length > 1 ? 's' : '' }}
          </div>
        </v-card>
      </div>

      <!-- ===================== PRICING & MARKUP SECTION ===================== -->
      <div class="content-section">
        <v-card class="content-card" elevation="2" rounded="xl">
          <v-card-title class="card-header">
            <div class="d-flex align-center">
              <div class="header-icon mr-3 teal--text" style="background: rgba(0,150,136,0.1);">
                <v-icon large>mdi-percent</v-icon>
              </div>
              <div>
                <div class="text-h6 font-weight-bold">Pricing & Markup Tool</div>
                <div class="text-caption grey--text">
                  Estimate your selling margin — a calculator only, it does not change your displayed price.
                </div>
              </div>
            </div>
          </v-card-title>

          <v-divider></v-divider>

          <v-card-text class="pa-4">
            <v-layout row wrap align-center>
              <v-flex xs12 md5>
                <v-text-field
                  v-model.number="globalMarkup"
                  type="number"
                  label="Global markup (%)"
                  outlined
                  dense
                  rounded
                  suffix="%"
                  prepend-inner-icon="mdi-percent-outline"
                  hint="Applied as a default reference for all your products"
                  persistent-hint
                ></v-text-field>
              </v-flex>
              <v-flex xs12 md6 offset-md1>
                <div class="pa-4 rounded-lg d-flex justify-space-between align-center" style="background:#f5f7fa;">
                  <div>
                    <div class="text-caption grey--text">Wholesale vs Selling price</div>
                    <div class="text-body-2 font-weight-medium">
                      Margin per unit (at current price)
                    </div>
                  </div>
                  <div class="text-h6 font-weight-bold teal--text">
                    +{{ computedGlobalMargin }} FCFA
                  </div>
                </div>
              </v-flex>
              <v-flex xs12 class="text-right mt-2">
                <v-btn
                  color="teal"
                  dark
                  rounded
                  :loading="savingMarkup"
                  :disabled="savingMarkup"
                  class="text-none font-weight-bold"
                  @click="saveGlobalMarkup"
                >
                  <v-icon left small>mdi-content-save</v-icon>
                  Save Global Markup
                </v-btn>
              </v-flex>
            </v-layout>

            <v-alert type="info" text dense class="mt-3 mb-0" border="left">
              Your markup is a reference calculation added on top of the wholesale price.
              The price paid by buyers is not modified automatically.
            </v-alert>
          </v-card-text>
        </v-card>
      </div>
      <!-- ===================== END PRICING & MARKUP ===================== -->

      <!-- ===================== TRANSACTION HISTORY SECTION ===================== -->
      <div class="content-section">
        <v-card class="content-card" elevation="2" rounded="xl">

          <!-- Header avec onglets -->
          <div class="history-header">
            <div class="d-flex align-center">
              <div class="header-icon mr-3" style="background: rgba(103,126,234,0.1); border-radius:12px; width:48px; height:48px; display:flex; align-items:center; justify-content:center;">
                <v-icon large color="primary">mdi-history</v-icon>
              </div>
              <div>
                <div class="text-h6 font-weight-bold">Transaction History</div>
                <div class="text-caption grey--text">All your earnings and transfers</div>
              </div>
            </div>

            <v-spacer></v-spacer>

            <!-- Tabs filtres -->
            <div class="history-tabs">
              <button
                v-for="tab in historyTabs"
                :key="tab.value"
                class="history-tab"
                :class="{ 'history-tab--active': activeHistoryTab === tab.value }"
                @click="activeHistoryTab = tab.value"
              >
                <v-icon x-small class="mr-1">{{ tab.icon }}</v-icon>
                {{ tab.label }}
                <span class="tab-count" v-if="tabCount(tab.value) > 0">{{ tabCount(tab.value) }}</span>
              </button>
            </div>
          </div>

          <v-divider></v-divider>

          <!-- Loading -->
          <div v-if="transactionsLoading" class="pa-4">
            <v-skeleton-loader
              v-for="n in 4"
              :key="n"
              type="list-item-two-line"
              class="mb-2"
            ></v-skeleton-loader>
          </div>

          <!-- Empty -->
          <div v-else-if="!filteredTransactions.length" class="empty-state-container" style="padding: 40px 20px;">
            <div class="empty-state-content">
              <div class="empty-illustration">
                <v-icon size="48" color="grey lighten-2">mdi-receipt-text-outline</v-icon>
              </div>
              <h3 class="text-subtitle-1 grey--text text--darken-2 font-weight-medium">No transactions yet</h3>
              <p class="text-caption grey--text mt-1">
                {{ activeHistoryTab === 'ALL' ? 'Your transactions will appear here.' : 'No transactions in this category.' }}
              </p>
            </div>
          </div>

          <!-- Liste des transactions -->
          <div v-else class="transactions-list">
            <div
              v-for="(txn, i) in filteredTransactions"
              :key="txn.reference || i"
              class="txn-row"
              :class="{ 'txn-row--last': i === filteredTransactions.length - 1 }"
            >
              <!-- Icône type -->
              <div class="txn-icon-wrap" :class="txnBgClass(txn.transaction_type)">
                <v-icon small :color="txnColor(txn.transaction_type)">
                  {{ txnIcon(txn.transaction_type) }}
                </v-icon>
              </div>

              <!-- Info principale -->
              <div class="txn-main">
                <div class="txn-title">{{ txnLabel(txn.transaction_type) }}</div>
                <div class="txn-sub">
                  <span v-if="txn.product">{{ txn.product.name || ('Product #' + txn.product) }}</span>
                  <span v-else-if="txn.description">{{ txn.description }}</span>
                  <span v-else class="grey--text">—</span>
                </div>
              </div>

              <!-- Ref + Date -->
              <div class="txn-meta hidden-sm-and-down">
                <div class="txn-ref">{{ txn.reference }}</div>
                <div class="txn-date">{{ txn.get_date_str || formatDate(txn.creation_date) }}</div>
              </div>

              <!-- Badge statut -->
              <div class="txn-status">
                <span class="status-dot" :class="statusDotClass(txn.status)"></span>
                <span class="status-label" :class="statusTextClass(txn.status)">{{ txn.status }}</span>
              </div>

              <!-- Montant -->
              <div class="txn-amount" :class="txn.transaction_type === 'SALE' ? 'amount--positive' : 'amount--negative'">
                {{ txn.transaction_type === 'SALE' ? '+' : '-' }}{{ formatCurrency(txn.amount) }}
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div v-if="filteredTransactions.length > 0" class="history-footer">
            <span class="text-caption grey--text">
              {{ filteredTransactions.length }} transaction{{ filteredTransactions.length > 1 ? 's' : '' }} displayed
            </span>
          </div>

        </v-card>
      </div>
      <!-- ===================== END TRANSACTION HISTORY ===================== -->

      <!-- ===================== SALES BREAKDOWN SECTION ===================== -->
      <div class="content-section">
        <v-card class="content-card" elevation="2" rounded="xl">
          <v-card-title class="card-header">
            <div class="d-flex align-center">
              <div class="header-icon mr-3 warning--text" style="background: rgba(255,152,0,0.1);">
                <v-icon large>mdi-calculator-variant</v-icon>
              </div>
              <div>
                <div class="text-h6 font-weight-bold">Sales Breakdown</div>
                <div class="text-caption grey--text">
                  See exactly how each sale is split: price → platform commission → reseller share → your net.
                </div>
              </div>
            </div>
          </v-card-title>

          <v-divider></v-divider>

          <div v-if="breakdownLoading" class="pa-4">
            <v-skeleton-loader
              v-for="n in 3"
              :key="n"
              type="list-item-three-line"
              class="mb-2"
            ></v-skeleton-loader>
          </div>

          <div v-else-if="!breakdown.length" class="empty-state-container" style="padding: 40px 20px;">
            <div class="empty-state-content">
              <div class="empty-illustration">
                <v-icon size="48" color="grey lighten-2">mdi-calculator-variant-outline</v-icon>
              </div>
              <h3 class="text-subtitle-1 grey--text text--darken-2 font-weight-medium">No sales yet</h3>
              <p class="text-caption grey--text mt-1">Breakdowns appear here after your first sale.</p>
            </div>
          </div>

          <div v-else class="breakdown-list">
            <div
              v-for="(row, i) in breakdown"
              :key="row.id || i"
              class="breakdown-row"
            >
              <div class="breakdown-head">
                <div class="d-flex align-center">
                  <v-icon small color="primary" class="mr-2">mdi-package-variant</v-icon>
                  <span class="breakdown-product">{{ row.product ? row.product.name : 'Product' }}</span>
                  <v-chip x-small class="ml-2">x{{ row.quantity }}</v-chip>
                  <v-chip v-if="row.via_reseller" x-small color="indigo" dark class="ml-2">
                    <v-icon x-small left>mdi-account-arrow-right</v-icon>
                    Reseller: {{ row.reseller_name }}
                  </v-chip>
                </div>
                <div class="text-caption grey--text">{{ row.get_date_str }}</div>
              </div>

              <div class="breakdown-flow">
                <div class="flow-step flow-gross">
                  <div class="flow-label">Sale price</div>
                  <div class="flow-value">{{ formatCurrency(row.base_amount) }}</div>
                </div>
                <v-icon small color="grey" class="flow-arrow">mdi-arrow-right</v-icon>
                <div class="flow-step flow-commission">
                  <div class="flow-label">Platform commission</div>
                  <div class="flow-value">-{{ formatCurrency(row.commission_amount) }}</div>
                </div>
                <v-icon v-if="row.via_reseller" small color="grey" class="flow-arrow">mdi-arrow-right</v-icon>
                <div v-if="row.via_reseller" class="flow-step flow-reseller">
                  <div class="flow-label">Reseller share</div>
                  <div class="flow-value">-{{ formatCurrency(row.reseller_amount) }}</div>
                </div>
                <v-icon small color="grey" class="flow-arrow">mdi-arrow-right</v-icon>
                <div class="flow-step flow-net">
                  <div class="flow-label">Your net</div>
                  <div class="flow-value">{{ formatCurrency(row.seller_amount) }}</div>
                </div>
              </div>
            </div>
          </div>
        </v-card>
      </div>
      <!-- ===================== END SALES BREAKDOWN ===================== -->

      <!-- Product Dialog -->
      <v-dialog v-model="productDialog" max-width="500px" rounded="xl">
        <v-card v-if="selectedProduct" rounded="xl">
          <v-card-title class="dialog-header primary white--text py-4">
            <v-icon left color="white" class="mr-2">mdi-package-variant</v-icon>
            Manage Product
            <v-spacer></v-spacer>
            <v-btn icon dark @click="closeProductDialog">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          
          <v-card-text class="pt-6">
            <div class="product-preview-dialog text-center mb-6">
              <v-avatar size="120" rounded="xl" class="mb-4" style="border: 2px solid #e0e0e0;">
                <v-img :src="selectedProduct.get_image || '/placeholder-product.png'" contain>
                  <template v-slot:placeholder>
                    <v-icon size="40" color="grey lighten-2">mdi-image-off</v-icon>
                  </template>
                </v-img>
              </v-avatar>
              <h3 class="text-h6 font-weight-bold mb-1">{{ selectedProduct.name }}</h3>
              <div class="text-h5 primary--text font-weight-bold mb-3">
                {{ formatCurrency(selectedProduct.price) }}
              </div>
              <div class="d-flex justify-center gap-2">
                <v-chip 
                  small 
                  :color="getStockColor(selectedProduct.quantity)" 
                  text-color="white"
                  class="mr-2"
                >
                  <v-icon x-small left>mdi-package-variant</v-icon>
                  Stock: {{ selectedProduct.quantity }}
                </v-chip>
                <v-chip small color="success" text-color="white">
                  <v-icon x-small left>mdi-check-circle</v-icon>
                  Sold: {{ selectedProduct.no_bought || 0 }}
                </v-chip>
              </div>
            </div>

            <v-divider class="mb-4"></v-divider>

            <div class="dialog-actions">
              <v-btn 
                block 
                large 
                color="primary" 
                class="mb-3 text-none font-weight-bold"
                :to="'/market_place/items/' + selectedProduct.id"
                @click="closeProductDialog"
                rounded
                elevation="2"
              >
                <v-icon left>mdi-eye</v-icon>
                Preview Product Page
              </v-btn>
              
              <v-btn 
                block 
                large 
                outlined 
                color="primary" 
                class="text-none font-weight-bold"
                :to="{ name: 'business_seller.add_product', query: { edit: selectedProduct.id } }"
                @click="closeProductDialog"
                rounded
              >
                <v-icon left>mdi-pencil</v-icon>
                Edit Product
              </v-btn>
            </div>
          </v-card-text>

          <v-card-actions class="pa-6 pt-0">
            <v-spacer></v-spacer>
            <v-btn 
              text 
              color="grey darken-1" 
              @click="closeProductDialog"
              class="text-none"
            >
              Close
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Markup Dialog -->
      <v-dialog v-model="markupDialog" max-width="420px" rounded="xl">
        <v-card v-if="selectedProductForMarkup" rounded="xl">
          <v-card-title class="dialog-header teal white--text py-4">
            <v-icon left color="white" class="mr-2">mdi-percent</v-icon>
            Product Markup
            <v-spacer></v-spacer>
            <v-btn icon dark @click="closeMarkupDialog">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text class="pt-6 pb-2">
            <p class="text-caption grey--text mb-4">
              Markup is a calculation reference on top of the wholesale price.
              It does not change the displayed selling price.
            </p>

            <h3 class="text-subtitle-1 font-weight-bold mb-1">{{ selectedProductForMarkup.name }}</h3>
            <div class="d-flex gap-2 mb-4">
              <v-chip small outlined>Price : {{ formatCurrency(selectedProductForMarkup.price) }}</v-chip>
              <v-chip small outlined>Wholesale : {{ formatCurrency(selectedProductForMarkup.wholesale_price) }}</v-chip>
            </div>

            <v-text-field
              v-model.number="productMarkupValue"
              type="number"
              label="Markup (%)"
              outlined
              dense
              rounded
              suffix="%"
              prepend-inner-icon="mdi-percent-outline"
            ></v-text-field>

            <v-alert v-if="markupError" type="error" dense text class="mt-2" border="left">
              {{ markupError }}
            </v-alert>
          </v-card-text>

          <v-card-actions class="pa-6 pt-0">
            <v-spacer></v-spacer>
            <v-btn text color="grey darken-1" @click="closeMarkupDialog" class="text-none font-weight-medium">
              Cancel
            </v-btn>
            <v-btn
              color="teal"
              dark
              :loading="savingProductMarkup"
              :disabled="savingProductMarkup"
              @click="saveProductMarkup"
              class="text-none font-weight-bold px-6"
              rounded
              elevation="2"
            >
              Save Markup
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Transfer Dialog -->
      <v-dialog v-model="transferDialog" max-width="500px" persistent>
        <v-card rounded="xl">
          <v-card-title class="dialog-header primary white--text py-4">
            <v-icon left color="white" class="mr-2">mdi-bank-transfer</v-icon>
            Transfer Balance
            <v-spacer></v-spacer>
            <v-btn icon dark @click="closeTransferDialog">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          
          <v-card-text class="pt-6 pb-2">
            <v-form ref="transferForm" v-model="transferFormValid" lazy-validation>
              <div class="balance-info pa-4 mb-4 rounded-lg" style="background: #f5f7fa;">
                <div class="d-flex justify-space-between align-center">
                  <span class="text-caption grey--text">Available Balance</span>
                  <span class="text-h6 font-weight-bold primary--text">
                    {{ formatCurrency(dashboard.seller_balance) }}
                  </span>
                </div>
              </div>

              <v-select
                v-model="transfer.destination"
                :items="destinationOptions"
                item-text="text"
                item-value="value"
                label="Transfer Destination"
                outlined
                dense
                rounded
                :rules="[v => !!v || 'Please select a destination']"
                prepend-inner-icon="mdi-target"
                class="mb-4"
              ></v-select>

              <v-select
                v-if="transfer.destination === 'CAMPAIGN'"
                v-model="transfer.campaign_id"
                :items="campaigns"
                item-text="name"
                item-value="id"
                label="Select Campaign"
                outlined
                dense
                rounded
                :rules="[v => !!v || 'Please select a campaign']"
                prepend-inner-icon="mdi-flag"
                class="mb-4"
                no-data-text="No campaigns available"
              ></v-select>

              <v-text-field
                v-model.number="transfer.amount"
                type="number"
                label="Amount to Transfer"
                outlined
                dense
                rounded
                prefix="FCFA"
                :rules="[
                  v => !!v || 'Amount is required',
                  v => v > 0 || 'Amount must be greater than 0',
                  v => v <= parseFloat(dashboard.seller_balance) || 'Insufficient balance'
                ]"
                prepend-inner-icon="mdi-currency-usd"
                hint="Enter amount in FCFA"
                persistent-hint
              ></v-text-field>

              <v-alert 
                v-if="transferError" 
                type="error" 
                dense 
                text 
                class="mt-4"
                border="left"
              >
                {{ transferError }}
              </v-alert>
            </v-form>
          </v-card-text>

          <v-card-actions class="pa-6 pt-0">
            <v-spacer></v-spacer>
            <v-btn 
              text 
              color="grey darken-1" 
              @click="closeTransferDialog"
              class="text-none font-weight-medium"
            >
              Cancel
            </v-btn>
            <v-btn 
              color="primary" 
              :loading="transferring"
              :disabled="!transferFormValid || transferring"
              @click="doTransfer"
              class="text-none font-weight-bold px-6"
              rounded
              elevation="2"
            >
              Confirm Transfer
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Success Snackbar -->
      <v-snackbar
        v-model="showSuccessSnackbar"
        color="success"
        top
        right
        timeout="4000"
        rounded="lg"
      >
        <div class="d-flex align-center">
          <v-icon class="mr-2">mdi-check-circle</v-icon>
          <span>{{ successMessage }}</span>
        </div>
      </v-snackbar>
    </v-container>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'SellerDashboard',

  data: () => ({
    dashboard: {
      seller_balance: '0.00',
      total_earned: '0.00',
      product_count: 0,
      transactions: [],
    },
    products: [],
    campaigns: [],
    transactions: [],
    transactionsLoading: false,
    activeHistoryTab: 'ALL',
    historyTabs: [
      { label: 'All',       value: 'ALL',               icon: 'mdi-format-list-bulleted' },
      { label: 'Earnings',  value: 'SALE',               icon: 'mdi-cash-plus' },
      { label: 'Transfers', value: 'TRANSFER',            icon: 'mdi-bank-transfer' },
    ],
    productSearch: '',
    productsLoading: false,
    productsError: null,
    breakdown: [],
    breakdownLoading: false,
    globalMarkup: 0,
    savingMarkup: false,
    markupDialog: false,
    selectedProductForMarkup: null,
    productMarkupValue: 0,
    savingProductMarkup: false,
    markupError: null,
    transferDialog: false,
    transferring: false,
    transferError: null,
    transferFormValid: false,
    showSuccessSnackbar: false,
    successMessage: '',
    productDialog: false,
    selectedProduct: null,
    transfer: {
      destination: 'WALLET',
      campaign_id: null,
      amount: '',
    },
    destinationOptions: [
      { text: 'My Wallet (Instant)', value: 'WALLET', icon: 'mdi-wallet' },
      { text: 'Savings Campaign',    value: 'CAMPAIGN', icon: 'mdi-flag' }
    ],
  }),

  computed: {
    filteredProducts() {
      const q = (this.productSearch || '').toLowerCase().trim()
      if (!q) return this.products
      return this.products.filter(p =>
        String(p.name || '').toLowerCase().includes(q) ||
        String(p.category || '').toLowerCase().includes(q)
      )
    },

    filteredTransactions() {
      if (this.activeHistoryTab === 'ALL') return this.transactions
      if (this.activeHistoryTab === 'TRANSFER') {
        return this.transactions.filter(t =>
          t.transaction_type === 'TRANSFER_WALLET' ||
          t.transaction_type === 'TRANSFER_CAMPAIGN'
        )
      }
      return this.transactions.filter(t => t.transaction_type === this.activeHistoryTab)
    },

    computedGlobalMargin() {
      const markup = parseFloat(this.globalMarkup) || 0
      let base = 0
      if (this.products.length) {
        const p = this.products[0]
        base = parseInt(p.wholesale_price) || parseInt(p.price) || 0
      }
      return Math.round(base * markup / 100)
    },
  },

  mounted() {
    document.title = 'Seller Dashboard | Business Hub'
    this.loadAll()
  },

  methods: {
    async loadAll() {
      await Promise.all([
        this.loadDashboard(),
        this.loadProducts(),
        this.loadCampaigns(),
        this.loadTransactions(),
        this.loadBreakdown(),
      ])
    },

    async loadDashboard() {
      try {
        const res = await axios.get('/api/v1/market_place/seller/dashboard/')
        this.dashboard = res.data
        if (res.data.markup_percentage !== undefined && res.data.markup_percentage !== null) {
          this.globalMarkup = parseFloat(res.data.markup_percentage) || 0
        }
      } catch (err) {
        if (err.response?.status === 403) {
          this.$router.push('/business-account/market_place/seller/apply')
        }
      }
    },

    async loadBreakdown() {
      this.breakdownLoading = true
      try {
        const res = await axios.get('/api/v1/market_place/seller/sales-breakdown/')
        this.breakdown = res.data.breakdown || []
      } catch {
        this.breakdown = []
      } finally {
        this.breakdownLoading = false
      }
    },

    async saveGlobalMarkup() {
      const markup = parseFloat(this.globalMarkup) || 0
      if (markup < 0) {
        this.successMessage = 'Markup cannot be negative.'
        this.showSuccessSnackbar = true
        return
      }
      this.savingMarkup = true
      try {
        const res = await axios.post('/api/v1/market_place/seller/markup/', { markup_percentage: markup })
        this.successMessage = res.data.message || 'Global markup updated!'
        this.showSuccessSnackbar = true
      } catch (err) {
        this.successMessage = err.response?.data?.error || 'Unable to save markup.'
        this.showSuccessSnackbar = true
      } finally {
        this.savingMarkup = false
      }
    },

    // ---- Product markup dialog ----
    openMarkupDialog(product) {
      this.selectedProductForMarkup = product
      this.productMarkupValue = parseFloat(product.markup_percentage) || 0
      this.markupError = null
      this.markupDialog = true
    },
    closeMarkupDialog() {
      this.markupDialog = false
      this.selectedProductForMarkup = null
      this.productMarkupValue = 0
      this.markupError = null
    },
    async saveProductMarkup() {
      const markup = parseFloat(this.productMarkupValue) || 0
      if (markup < 0) {
        this.markupError = 'Markup cannot be negative.'
        return
      }
      this.savingProductMarkup = true
      this.markupError = null
      try {
        const res = await axios.post(
          `/api/v1/market_place/seller/products/${this.selectedProductForMarkup.id}/markup/`,
          { markup_percentage: markup }
        )
        this.successMessage = res.data.message || 'Product markup updated!'
        this.showSuccessSnackbar = true
        this.markupDialog = false
        await this.loadProducts()
      } catch (err) {
        this.markupError = err.response?.data?.error || 'Unable to update markup.'
      } finally {
        this.savingProductMarkup = false
      }
    },

    async loadProducts() {
      this.productsLoading = true
      this.productsError = null
      try {
        const res = await axios.get('/api/v1/market_place/seller/products/')
        this.products = Array.isArray(res.data) ? res.data : []
        if (!Array.isArray(res.data)) this.productsError = 'Unexpected response format from server.'
      } catch (err) {
        this.products = []
        this.productsError = err.response?.data?.error || 'Unable to load products. Please try again.'
      } finally {
        this.productsLoading = false
      }
    },

    async loadCampaigns() {
      try {
        const res = await axios.get('/api/v1/market_place/seller/campaigns/')
        this.campaigns = res.data || []
      } catch { this.campaigns = [] }
    },

    async loadTransactions() {
      this.transactionsLoading = true
      try {
        const res = await axios.get('/api/v1/market_place/seller/transactions/')
        this.transactions = Array.isArray(res.data) ? res.data : (res.data?.results || [])
      } catch {
        this.transactions = []
      } finally {
        this.transactionsLoading = false
      }
    },

    tabCount(tabValue) {
      if (tabValue === 'ALL') return this.transactions.length
      if (tabValue === 'TRANSFER') {
        return this.transactions.filter(t =>
          t.transaction_type === 'TRANSFER_WALLET' || t.transaction_type === 'TRANSFER_CAMPAIGN'
        ).length
      }
      return this.transactions.filter(t => t.transaction_type === tabValue).length
    },

    // ---- Product Dialog ----
    openProductDialog(product) {
      this.selectedProduct = product
      this.productDialog = true
    },
    closeProductDialog() {
      this.productDialog = false
      this.selectedProduct = null
    },

    // ---- Transfer Dialog ----
    openTransferDialog() {
      this.resetTransferForm()
      this.transferDialog = true
    },
    closeTransferDialog() {
      this.transferDialog = false
      this.resetTransferForm()
    },
    resetTransferForm() {
      this.transfer = { destination: 'WALLET', campaign_id: null, amount: '' }
      this.transferError = null
      if (this.$refs.transferForm) this.$refs.transferForm.resetValidation()
    },

    async doTransfer() {
      if (!this.$refs.transferForm.validate()) return
      this.transferError = null
      this.transferring = true
      try {
        const payload = { destination: this.transfer.destination, amount: this.transfer.amount }
        if (this.transfer.destination === 'CAMPAIGN') payload.campaign_id = this.transfer.campaign_id
        const res = await axios.post('/api/v1/market_place/seller/transfer/', payload)
        this.successMessage = res.data.message || 'Transfer completed successfully!'
        this.showSuccessSnackbar = true
        this.transferDialog = false
        this.resetTransferForm()
        await Promise.all([this.loadDashboard(), this.loadTransactions()])
      } catch (err) {
        this.transferError = err.response?.data?.error || 'Transfer failed. Please try again.'
      } finally {
        this.transferring = false
      }
    },

    // ---- Helpers Transactions ----
    txnColor(type) {
      const map = {
        SALE: 'success',
        TRANSFER_WALLET: 'primary',
        TRANSFER_CAMPAIGN: 'warning',
        REFUND: 'grey',
      }
      return map[type] || 'grey'
    },
    txnBgClass(type) {
      const map = {
        SALE: 'txn-bg--success',
        TRANSFER_WALLET: 'txn-bg--primary',
        TRANSFER_CAMPAIGN: 'txn-bg--warning',
        REFUND: 'txn-bg--grey',
      }
      return map[type] || 'txn-bg--grey'
    },
    txnIcon(type) {
      const map = {
        SALE: 'mdi-cash-plus',
        TRANSFER_WALLET: 'mdi-wallet-outline',
        TRANSFER_CAMPAIGN: 'mdi-flag-outline',
        REFUND: 'mdi-cash-refund',
      }
      return map[type] || 'mdi-help-circle'
    },
    txnLabel(type) {
      const map = {
        SALE: 'Product Sale',
        TRANSFER_WALLET: 'Transfer to Wallet',
        TRANSFER_CAMPAIGN: 'Transfer to Campaign',
        REFUND: 'Refund',
      }
      return map[type] || type
    },
    statusDotClass(status) {
      const map = { COMPLETED: 'dot--success', PENDING: 'dot--warning', FAILED: 'dot--error' }
      return map[status] || 'dot--grey'
    },
    statusTextClass(status) {
      const map = { COMPLETED: 'success--text', PENDING: 'warning--text', FAILED: 'error--text' }
      return map[status] || 'grey--text'
    },

    // ---- Format ----
    formatCurrency(value) {
      if (!value) return '0 FCFA'
      return new Intl.NumberFormat('en-US').format(parseFloat(value)) + ' FCFA'
    },
    formatDate(dateStr) {
      if (!dateStr) return '—'
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric'
      })
    },
    getStockColor(quantity) {
      if (quantity <= 0) return 'error'
      if (quantity <= 5) return 'warning'
      return 'success'
    },
  },
}
</script>

<style scoped>
.seller-dashboard {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%);
}

/* ===== HERO ===== */
.hero-section {
  position: relative;
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 50%, #667eea 100%);
  border-radius: 24px;
  padding: 40px;
  color: white;
  overflow: hidden;
  margin-bottom: 32px;
  box-shadow: 0 20px 40px rgba(30, 60, 114, 0.3);
}
.hero-content { position: relative; z-index: 2; max-width: 600px; }
.hero-badge {
  display: inline-flex; align-items: center;
  padding: 6px 16px;
  background: rgba(255,255,255,0.2); backdrop-filter: blur(10px);
  border-radius: 50px; font-size: 12px; font-weight: 600;
  letter-spacing: 1px; text-transform: uppercase;
  margin-bottom: 16px; border: 1px solid rgba(255,255,255,0.3);
}
.hero-title { font-size: 36px; font-weight: 800; margin-bottom: 12px; line-height: 1.2; text-shadow: 0 2px 4px rgba(0,0,0,0.1); }
.hero-subtitle { font-size: 16px; opacity: 0.9; margin-bottom: 24px; line-height: 1.6; }
.hero-actions { display: flex; gap: 16px; }
.glass-btn {
  background: rgba(255,255,255,0.1) !important;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255,255,255,0.3) !important;
}
.hero-decoration {
  position: absolute; right: -100px; top: -100px;
  width: 400px; height: 400px;
  background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
  border-radius: 50%; z-index: 1;
}

/* ===== STATS ===== */
.stats-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 24px; margin-bottom: 32px; }
.stat-card { padding: 24px; position: relative; overflow: hidden; transition: transform .3s ease, box-shadow .3s ease; }
.stat-card:hover { transform: translateY(-4px); box-shadow: 0 12px 24px rgba(0,0,0,0.1) !important; }
.stat-balance { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; }
.stat-secondary { background: white; }
.stat-bg-icon { position: absolute; right: -20px; bottom: -20px; opacity: 0.1; z-index: 0; }
.stat-content { position: relative; z-index: 1; }
.stat-header { display: flex; justify-content: space-between; align-items: center; }
.stat-icon-wrapper { width: 48px; height: 48px; border-radius: 12px; display: flex; align-items: center; justify-content: center; }
.primary-gradient { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); }
.success-gradient { background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%); }
.info-gradient    { background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%); }
.stat-label { font-size: 12px; letter-spacing: .5px; }
.opacity-8 { opacity: .9; }

/* ===== CONTENT CARDS ===== */
.content-section { margin-bottom: 24px; }
.content-card { background: white; border-radius: 20px !important; overflow: hidden; }
.card-header { padding: 24px; }
.header-icon {
  width: 48px; height: 48px;
  background: rgba(103,126,234,0.1); border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
}

/* ===== PRODUCTS TABLE ===== */
.table-container { overflow-x: auto; width: 100%; }
.products-table { width: 100%; border-collapse: collapse; }
.products-table th {
  font-size: 11px; text-transform: uppercase; letter-spacing: .5px;
  color: #64748b; font-weight: 600;
  border-bottom: 1px solid #e2e8f0;
  padding: 16px; background: #fafbff; white-space: nowrap;
}
.products-table td { padding: 16px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; }
.product-row { transition: background-color .2s; }
.product-row:hover { background-color: #f8fafc; }
.product-avatar { border: 1px solid #e2e8f0; background: white; }

/* ===== HISTORY SECTION ===== */
.history-header {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  padding: 24px;
}

.history-tabs {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.history-tab {
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 500;
  border: 1.5px solid #e2e8f0;
  background: white;
  color: #64748b;
  cursor: pointer;
  transition: all .2s ease;
  outline: none;
}
.history-tab:hover { border-color: #667eea; color: #667eea; background: #f0f4ff; }
.history-tab--active {
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  border-color: transparent;
  box-shadow: 0 4px 12px rgba(102,126,234,0.35);
}
.tab-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  border-radius: 50px;
  font-size: 10px;
  font-weight: 700;
  margin-left: 6px;
  padding: 0 5px;
  background: rgba(255,255,255,0.3);
}
.history-tab:not(.history-tab--active) .tab-count {
  background: #e2e8f0;
  color: #64748b;
}

/* ===== TRANSACTION ROWS ===== */
.transactions-list { padding: 0 8px; }

.txn-row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  border-bottom: 1px solid #f1f5f9;
  transition: background .15s;
}
.txn-row:hover { background: #fafbff; }
.txn-row--last { border-bottom: none; }

.txn-icon-wrap {
  width: 40px; height: 40px; border-radius: 12px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
}
.txn-bg--success  { background: rgba(17,153,142,0.12); }
.txn-bg--primary  { background: rgba(102,126,234,0.12); }
.txn-bg--warning  { background: rgba(255,152,0,0.12); }
.txn-bg--grey     { background: rgba(100,116,139,0.1); }

.txn-main { flex: 1; min-width: 0; }
.txn-title { font-size: 14px; font-weight: 600; color: #1e293b; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.txn-sub   { font-size: 12px; color: #94a3b8; margin-top: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.txn-meta { text-align: right; flex-shrink: 0; min-width: 130px; }
.txn-ref  { font-size: 11px; color: #94a3b8; font-family: monospace; }
.txn-date { font-size: 11px; color: #b0bac5; margin-top: 2px; }

.txn-status {
  display: flex; align-items: center; gap: 5px;
  flex-shrink: 0;
}
.status-dot {
  width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0;
}
.dot--success { background: #11998e; }
.dot--warning { background: #ff9800; }
.dot--error   { background: #e53935; }
.dot--grey    { background: #94a3b8; }
.status-label { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .5px; }

.txn-amount {
  font-size: 15px; font-weight: 700; flex-shrink: 0; min-width: 120px; text-align: right;
}
.amount--positive { color: #11998e; }
.amount--negative { color: #e53935; }

.history-footer {
  padding: 12px 24px;
  border-top: 1px solid #f1f5f9;
  text-align: center;
}

/* ===== DIALOGS ===== */
.product-preview-dialog {
  background: linear-gradient(135deg, #f0f4ff 0%, #f5f3ff 100%);
  padding: 24px; border-radius: 16px; border: 1px solid #e0e7ff;
}
.dialog-actions { display: flex; flex-direction: column; gap: 12px; }
.dialog-header { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important; }

/* ===== EMPTY STATES ===== */
.empty-state-container { padding: 60px 20px; display: flex; justify-content: center; align-items: center; }
.empty-state-content { text-align: center; max-width: 400px; }
.empty-illustration {
  width: 100px; height: 100px; background: #f8fafc; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  margin: 0 auto 24px; border: 2px dashed #e2e8f0;
}
.max-width-400 { max-width: 400px; }
.gap-2 { gap: 8px; }

/* ===== RESPONSIVE ===== */
@media (max-width: 960px) {
  .stats-grid { grid-template-columns: 1fr; }
  .hero-title { font-size: 28px; }
  .hero-section { padding: 24px; }
  .hero-actions { flex-direction: column; }
  .hero-actions .v-btn { margin-left: 0 !important; width: 100%; }
  .history-header { flex-direction: column; align-items: flex-start; }
}
@media (max-width: 600px) {
  .card-header { flex-direction: column; align-items: flex-start !important; gap: 16px; }
  .search-field { width: 100%; max-width: none !important; }
  .products-table th, .products-table td { padding: 12px 8px; }
  .txn-row { gap: 10px; padding: 14px 12px; }
  .txn-amount { min-width: 90px; font-size: 13px; }
}

/* ===== SALES BREAKDOWN ===== */
.breakdown-list { padding: 0 8px; }
.breakdown-row { padding: 16px 20px; border-bottom: 1px solid #f1f5f9; }
.breakdown-row:last-child { border-bottom: none; }
.breakdown-head {
  display: flex; justify-content: space-between; align-items: center;
  flex-wrap: wrap; gap: 8px; margin-bottom: 12px;
}
.breakdown-product { font-weight: 600; color: #1e293b; }
.breakdown-flow { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.flow-step {
  display: flex; flex-direction: column;
  padding: 8px 14px; border-radius: 10px; min-width: 110px;
}
.flow-label { font-size: 10px; text-transform: uppercase; letter-spacing: .4px; opacity: .75; }
.flow-value { font-size: 14px; font-weight: 700; }
.flow-gross      { background: #f1f5f9; color: #334155; }
.flow-commission { background: #fff3e0; color: #b26a00; }
.flow-reseller   { background: #e8eaf6; color: #303f9f; }
.flow-net        { background: #e0f2f1; color: #00695c; }
.flow-arrow { margin: 0 2px; }
</style>